import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import classnames from 'classnames';

import { formatCurrency, formatMaturity, formatPercents, ProductOffer } from '@savebetter/api';
import { Carousel, ProductCardProps, Spinner, TabContainer, TabHeader } from '@savebetter/components';

import { useProductOfferDocument } from '../hooks/useProductOffer';
import { buildPdf } from '../utils/buildPdf';

import { LinkProps } from './Link';
import { MediaProps } from './Media';
import { PageSectionBlok, PageSectionProps } from './PageSection';
import { ProductCardsSequencer } from './ProductCardsSequencer';
import { Section } from './Section';

// global variables
declare const postLoginUrl: string;

interface Blok extends PageSectionBlok {
  todo?: string;
  productTabs: ProductTab[];
}

export type ProductType = {
  productCode: string;
  prodTermsUrl: LinkProps;
  badgeText?: string;
};

type ProductTab = {
  _uid: string;
  title: string;
  products: ProductType[];
  media?: MediaProps[];
  text: string;
  apyLabel: string;
  buttonLabel: string;
  minBalanceLabel: string;
  prodTermsText: string;
};

type TabContainerItem = {
  visible: boolean;
  key: string;
  tabProducts: Array<PartialProductCard>;
  apyLabel: string;
  buttonLabel: string;
  minBalanceLabel: string;
  prodTermsText: string;
};

type ProductOffersQueryData = {
  productOffers: {
    nodes: Array<ProductOffer>;
  };
};

interface ProductCardsProps extends PageSectionProps {
  blok: Blok;
}

type PartialProductCard = Pick<
  ProductCardProps,
  | 'bankName'
  | 'productName'
  | 'apyValue'
  | 'minBalanceValue'
  | 'openProductTerms'
  | 'badgeText'
  | 'logoSrc'
  | 'logoAlt'
  | 'maturityValue'
  | 'buttonHref'
>;

const PRODUCT_CARDS_QUERY = gql`
  {
    productOffers {
      nodes {
        id
        apy
        name
        type
        minAmount
        maturity {
          unit
          duration
        }
        productBank {
          logoUrl
          name
        }
      }
    }
  }
`;

const tabCarouselSettings = {
  lg: {
    slidesPerPage: 3
  },
  md: {
    slidesPerPage: 3
  },
  sm: {
    slidesPerPage: 2,
    offset: 25
  }
};

export const ProductCards: FunctionComponent<React.PropsWithChildren<ProductCardsProps>> = ({
  blok,
  sectionClassName
}) => {
  const [activeTabId, setActiveTabId] = useState(blok.productTabs[0]._uid);
  const [productsTabs, setProductsTabs] = useState<Array<Partial<ProductTab>>>([]);
  const [productOffers, setProductOffers] = useState<Array<ProductOffer>>([]);
  const [tabContainers, setTabContainers] = useState<Array<TabContainerItem>>([]);
  const { loading, data, error } = useQuery<ProductOffersQueryData>(PRODUCT_CARDS_QUERY);
  const [getProductOfferDocument] = useProductOfferDocument();

  useEffect(() => {
    const filteredProductTabs = blok.productTabs.map(
      ({ _uid, products, minBalanceLabel, buttonLabel, apyLabel, prodTermsText }: ProductTab) => ({
        _uid,
        products,
        minBalanceLabel,
        buttonLabel,
        apyLabel,
        prodTermsText
      })
    );
    setProductsTabs(filteredProductTabs);
  }, [blok.productTabs]);

  useEffect(() => {
    if (data && data.productOffers) {
      setProductOffers(data.productOffers.nodes);
    }
  }, [data]);

  useEffect(() => {
    if (productOffers.length > 0 && productsTabs.length > 0) {
      let tabs: Array<TabContainerItem> = [];

      productsTabs.forEach((productsTab: Partial<ProductTab>) => {
        const { products = [], _uid, minBalanceLabel, buttonLabel, apyLabel, prodTermsText } = productsTab;

        // [key] : value equals to => [productId] : { badgeText, productTermsLinkUrl }
        let productsMapped: { [key: string]: { badgeText?: string; productTermsLinkUrl: string } } = {};
        products.forEach((product: ProductType) => {
          const { productCode, badgeText, prodTermsUrl } = product;
          productsMapped = {
            ...productsMapped,
            ...{
              [productCode]: {
                badgeText,
                productTermsLinkUrl: prodTermsUrl.url
              }
            }
          };
        });

        // Get ProductOffers exist in ProductTab
        const selectedOffers: Array<ProductOffer> = productOffers.filter(({ id }: { id: string }) =>
          Object.keys(productsMapped).includes(id)
        );

        if (selectedOffers.length > 0) {
          // Aggregate partial ProductOffer and ProductTab data to render
          const tabProducts: Array<PartialProductCard> = selectedOffers.map((selectedOffer: ProductOffer) => {
            const { badgeText } = productsMapped[selectedOffer.id];
            const {
              name: productName,
              productBank: { name: bankName, logoUrl },
              id,
              apy,
              minAmount,
              maturity,
              type
            } = selectedOffer;

            const openProductTerms = async (): Promise<string | void> => {
              const { data: productTerms, error } = await getProductOfferDocument({ variables: { productId: id } });
              const base64Content = productTerms?.productOfferDocument?.content;
              if (error) {
                throw error;
              }
              if (!base64Content) {
                throw new Error('Product offer document is empty or null');
              }
              return buildPdf(base64Content);
            };

            return {
              bankName,
              productName,
              apyValue: formatPercents(Number(apy) / 100),
              minBalanceValue: formatCurrency(minAmount, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
              }),
              openProductTerms,
              badgeText,
              logoSrc: logoUrl || '',
              logoAlt: bankName,
              buttonHref: `${postLoginUrl}/auth/register?productId=${id}`,
              maturityValue: maturity && type !== 'SAVINGS_ACCOUNT' ? formatMaturity(maturity) : undefined
            };
          });

          tabs = [
            ...tabs,
            {
              key: _uid as string,
              visible: _uid === activeTabId,
              tabProducts,
              minBalanceLabel: minBalanceLabel as string,
              buttonLabel: buttonLabel as string,
              apyLabel: apyLabel as string,
              prodTermsText: prodTermsText as string
            }
          ];
        }
      });

      setTabContainers(tabs);
    }
  }, [productOffers, productsTabs, activeTabId, getProductOfferDocument]);

  const prepareProduct = (
    product: PartialProductCard,
    minBalanceLabel: string,
    buttonLabel: string,
    apyLabel: string,
    prodTermsText: string
  ): ProductCardProps | undefined => {
    if (!product) {
      return;
    }

    return {
      ...product,
      productTermsLinkText: prodTermsText,
      minBalanceLabel,
      buttonLabel,
      apyLabel
    };
  };

  const getTabHeaders = (): ReactElement[] =>
    blok.productTabs.map(({ _uid, title, media }) => (
      <TabHeader
        isActive={activeTabId === _uid}
        onClick={(): void => setActiveTabId(_uid)}
        title={title}
        src={media && media[0].img}
        alt={media && media[0].alt}
        key={_uid}
        isCircleImg={false}
      />
    ));

  const getContent = (): React.ReactNode => {
    if (loading || error) {
      return (
        <div className="flex justify-center">
          <Spinner />
        </div>
      );
    }
    if (tabContainers.length === 0) {
      return null;
    }
    return (
      <>
        <Carousel
          settings={tabCarouselSettings}
          contentLabel="Product type tabs"
          showIndicators={false}
          useDefaultSlideWrapper={false}
          slides={getTabHeaders()}
          className={'border-b-black-10 border-b-2 md:mx-12'}
        />
        {tabContainers.map(
          ({ visible, key, tabProducts, minBalanceLabel, buttonLabel, apyLabel, prodTermsText }: TabContainerItem) => (
            <TabContainer visible={visible} key={key}>
              <ProductCardsSequencer
                slidesData={tabProducts.map((product: PartialProductCard) =>
                  prepareProduct(product, minBalanceLabel, buttonLabel, apyLabel, prodTermsText)
                )}
              />
            </TabContainer>
          )
        )}
      </>
    );
  };

  return (
    <Section
      width="md"
      hasBackground={blok.hasBackground}
      hasBorder={blok.hasBorder}
      className={classnames('lg:mx-16 mx-0 py-0', sectionClassName)}
      hasPaddingBottom={false}
      hasPaddingTop={false}
    >
      {getContent()}
    </Section>
  );
};
